import React, { useCallback, useEffect, useMemo, useState } from "react"
import Select from "react-select"
import axios from "axios"
import { toast } from "react-toastify"
import { BASE_URL } from "../../global"
import {
  getAdminShellToken,
  getViewAsTutorKey,
  getActAsTutorName,
  isAdminMode,
  setViewAsTutor,
  TUTOR_ADMIN_VIEW_CHANGED,
} from "../../common/auth/tutor-admin-auth"

const buildTutorLabel = tutor => {
  const name = `${tutor.tut_fname || ""} ${tutor.tut_surname || ""}`.trim()
  if (name && tutor.ta_login_id) {
    return `${name} (${tutor.ta_login_id})`
  }
  return tutor.display_name || name || tutor.ta_login_id || "Unknown tutor"
}

const TutorAdminTutorPicker = ({ onSelected, className = "" }) => {
  const [options, setOptions] = useState([])
  const [selected, setSelected] = useState(null)
  const [loadingTutors, setLoadingTutors] = useState(false)
  const [selecting, setSelecting] = useState(false)

  const syncSelectedFromStorage = useCallback((tutorOptions) => {
    const currentKey = getViewAsTutorKey()
    if (!currentKey) {
      setSelected(null)
      return
    }

    const match =
      tutorOptions.find(option => option.value === currentKey) || {
        value: currentKey,
        label: getActAsTutorName() || currentKey,
      }

    setSelected(match)
  }, [])

  const loadTutors = useCallback(async () => {
    if (!isAdminMode()) {
      return
    }

    setLoadingTutors(true)
    try {
      const token = getAdminShellToken()
      if (!token) {
        toast.error("Admin session expired. Exit Admin and try again.")
        setOptions([])
        setSelected(null)
        return
      }
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`
      const response = await axios.post(`${BASE_URL}tutor-admin/tutors`)
      const tutors = response?.data?.data || []
      const tutorOptions = tutors.map(tutor => ({
        value: tutor.pk_tut_key,
        label: buildTutorLabel(tutor),
        tutor,
      }))

      setOptions(tutorOptions)
      syncSelectedFromStorage(tutorOptions)
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to load tutors for this centre"
      )
      setOptions([])
      setSelected(null)
    } finally {
      setLoadingTutors(false)
    }
  }, [syncSelectedFromStorage])

  useEffect(() => {
    loadTutors()

    const handleViewChanged = () => {
      if (isAdminMode()) {
        loadTutors()
      } else {
        setOptions([])
        setSelected(null)
      }
    }

    window.addEventListener(TUTOR_ADMIN_VIEW_CHANGED, handleViewChanged)
    return () => {
      window.removeEventListener(TUTOR_ADMIN_VIEW_CHANGED, handleViewChanged)
    }
  }, [loadTutors])

  const handleChange = async option => {
    if (!option?.value) {
      return
    }

    setSelecting(true)
    try {
      const token = getAdminShellToken()
      if (!token) {
        toast.error("Admin session expired. Exit Admin and try again.")
        return
      }
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`
      const response = await axios.post(`${BASE_URL}tutor-admin/view-as`, {
        view_as_tut_key: option.value,
      })

      if (response?.data?.success) {
        setViewAsTutor(
          response.data.token,
          response.data.view_as_tut_key,
          response.data.view_as_tut_name
        )
        // Work APIs must use the new impersonation token immediately.
        axios.defaults.headers.common["Authorization"] = `Bearer ${response.data.token}`
        setSelected({
          value: response.data.view_as_tut_key,
          label: response.data.view_as_tut_name || option.label,
        })
        onSelected?.(response.data)
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to select tutor")
    } finally {
      setSelecting(false)
    }
  }

  const selectStyles = useMemo(
    () => ({
      control: base => ({
        ...base,
        minHeight: 38,
      }),
      menu: base => ({
        ...base,
        zIndex: 20,
      }),
    }),
    []
  )

  if (!isAdminMode()) {
    return null
  }

  return (
    <div className={className}>
      <label className="form-label mb-1 fw-semibold">Select tutor</label>
      <Select
        className="cs-style"
        classNamePrefix="select2-selection"
        isSearchable
        isLoading={loadingTutors || selecting}
        options={options}
        value={selected}
        onChange={handleChange}
        placeholder="Search tutor by name or login..."
        noOptionsMessage={() => "No tutors found"}
        styles={selectStyles}
      />
      <small className="text-muted d-block mt-1">
        Search and pick a tutor. Full access for that tutor applies across the whole app.
      </small>
    </div>
  )
}

export default TutorAdminTutorPicker
